import { ContractId } from '../value-objects';
import { ProductId } from '@services/MarppleShop/features/ProductMeta';
import { ItemOrderId } from '@services/MarppleShop/features/Product/domain';
import { Money } from '../../../../../../shared/features/domain/value-objects/Money.ts';

export interface FundingContractEntityProps {
  id?: ContractId;
  productId: ProductId;
  itemOrderId: ItemOrderId;
  userId: number;
  money: Money;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * 펀딩 계약 엔티티
 * 개별 후원 계약의 생성, 취소, 상태 관리를 담당
 */
export class FundingContractEntity {
  private readonly _id?: ContractId;
  public readonly productId: ProductId;
  public readonly itemOrderId: ItemOrderId;
  public readonly userId: number;
  private _money: Money;
  public readonly createdAt: Date;
  private _updatedAt: Date;

  private constructor(props: FundingContractEntityProps) {
    this._id = props.id;
    this.productId = props.productId;
    this.itemOrderId = props.itemOrderId;
    this.userId = props.userId;
    this._money = props.money;
    this.createdAt = props.createdAt || new Date();
    this._updatedAt = props.updatedAt || new Date();
  }

  /**
   * 후원 계약 취소
   * 계약 금액을 음수로 변경하여 취소 상태 표시
   */
  cancel(): void {
    if (this._money.isNegative()) {
      throw new Error('이미 취소된 후원 계약입니다');
    }

    this._money = this._money.negate();
    this._updatedAt = new Date();
  }

  /**
   * 계약 취소 여부 확인
   */
  isCancelled(): boolean {
    return this._money.isNegative();
  }

  /**
   * 유효한 후원 금액 반환 (취소된 경우 음수)
   */
  getEffectiveAmount(): Money {
    return this._money;
  }

  get id(): ContractId | undefined {
    return this._id;
  }

  getIdOrThrow(): ContractId {
    if (!this._id) {
      throw new Error('초기화되지 않은 FundingContractEntity입니다.');
    }
    return this._id;
  }

  get money(): Money {
    return this._money;
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  static create(props: Omit<FundingContractEntityProps, 'id'>): FundingContractEntity {
    // 후원 계약 생성 시 검증
    if (props.money.isNegative() || props.money.isZero()) {
      throw new Error('후원 금액은 양수여야 합니다');
    }

    return new FundingContractEntity(props);
  }

  static from(props: FundingContractEntityProps): FundingContractEntity {
    return new FundingContractEntity(props);
  }
}
